import { useState } from 'react';
import { Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ImageManagementSection } from '@/components/shared/ImageManagementSection';
import { ImageGallery } from '@/components/shared/ImageGallery';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { useLeadImages } from '../hooks/useLeadImages';
import { useUploadLeadImages } from '../hooks/useUploadLeadImages';

export interface LeadImagesButtonProps {
  leadId: number;
  /** When false, upload and delete actions are hidden (read-only lead). */
  canEdit?: boolean;
}

/**
 * Compact image entry point for the Lead front page.
 *
 * Renders a camera icon plus a count; thumbnails and the upload control live
 * behind a dialog and are only mounted once the user opens it. This replaces
 * the inline ImageGallery + ImageUpload blocks that used to sit on the form.
 *
 * Two reasons, both from client feedback (Angie, Note 1): the inline gallery
 * issued one HTTP request per thumbnail on every lead load, and it consumed
 * space on a screen she wants condensed. Legacy ManageLeads.aspx:643-649 showed
 * only a camera icon and an "Add Image" button here — no previews.
 *
 * The image list itself is cheap metadata, so the count is fetched eagerly;
 * it is the thumbnail <img> requests that are deferred.
 */
export function LeadImagesButton({ leadId, canEdit = false }: LeadImagesButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { images, isLoading, error: fetchError, refetch, deleteImage } = useLeadImages(leadId);
  const { uploadImages, isUploading, error: uploadError } = useUploadLeadImages(leadId, refetch);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setIsOpen(true)}
        className="flex h-6 items-center gap-1.5 px-2 text-xs font-medium"
      >
        <Camera className="h-3.5 w-3.5 text-[#DC2626]" />
        Images ({isLoading ? '…' : images.length})
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Images</DialogTitle>
          </DialogHeader>
          {canEdit ? (
            <ImageManagementSection
              images={images}
              isLoading={isLoading}
              fetchError={fetchError}
              uploadError={uploadError}
              isUploading={isUploading}
              onUpload={uploadImages}
              onDelete={deleteImage}
              canDelete
            />
          ) : (
            // Read-only viewers get the gallery alone. ImageManagementSection
            // always renders its upload control, and this dialog is portalled
            // out of the form's disabled fieldset, so passing it through here
            // would hand a read-only user a working upload box.
            <>
              <ErrorAlert error={fetchError} className="mb-2" />
              <ImageGallery images={images} canDelete={false} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
